import { useQuery } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { fetcher } from '@/shared/api';
import type { ApiResponse } from '@/shared/types/api';
import { userKeys } from './queryKeys';
import type { ProfileData } from '../model/types';

function buildQs(params: Record<string, unknown>): string {
  return new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
    ) as Record<string, string>,
  ).toString();
}

export function useUserList(queryParams: Record<string, unknown> = {}) {
  const query = buildQs(queryParams);
  return useQuery({
    queryKey: userKeys.list(queryParams),
    queryFn: () => fetcher(`/users?${query}`),
  });
}

export function useUserRoleForSelect() {
  return useQuery({
    queryKey: userKeys.rolesForSelect(),
    queryFn: () => fetcher('/roles/list'),
  });
}

export function useUserDetails(id: string) {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => fetcher(`/users/${id}`),
    enabled: !!id,
  });
}

export function useMyProfile() {
  const { isAuthenticated } = useAuth0();
  return useQuery({
    queryKey: userKeys.profile,
    queryFn: () => fetcher<ApiResponse<ProfileData>>('/auth/profile'),
    enabled: isAuthenticated,
    retry: (failureCount, error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403) return false;
      if (!status) return false;
      return failureCount < 1;
    },
  });
}
